import React from "react";
import { Link } from "react-router-dom";
import { Scale, ShieldCheck } from "lucide-react";

const Section = ({ title, id, children, testId }) => (
    <section id={id} className="scroll-mt-24" data-testid={testId}>
        <h2 className="font-heading font-medium text-2xl tracking-tight text-foreground mb-3">{title}</h2>
        <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">{children}</div>
    </section>
);

export default function RefundPolicy() {
    return (
        <div className="bg-background" data-testid="refund-policy-page">
            <section className="pt-16 pb-6">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><Scale size={11} /> Policy</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">Refund Policy</h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        How subscriptions, verification purchases, and recruiting services are handled at Simonara Talent Solutions.
                    </p>
                </div>
            </section>

            <section className="pb-16">
                <div className="mx-auto max-w-4xl px-6 space-y-10">
                    <Section title="Employers · Software Subscription" id="employer-subscription" testId="refund-employer-sub">
                        <ul className="list-disc pl-5 space-y-1.5">
                            <li>Subscriptions are billed in advance.</li>
                            <li>Monthly and annual subscription fees are generally <b className="text-foreground">non-refundable</b> once a billing period begins.</li>
                            <li>Employers may cancel at any time. Cancellation stops future billing.</li>
                            <li>Access continues until the end of the paid billing period.</li>
                            <li>No partial refunds.</li>
                        </ul>
                    </Section>

                    <Section title="Verification Purchases" id="verification" testId="refund-verify">
                        <ul className="list-disc pl-5 space-y-1.5">
                            <li>Verification fees become <b className="text-foreground">non-refundable</b> once processing has started, because third-party providers immediately incur costs.</li>
                            <li>If a verification request has not yet begun processing, Simonara may cancel and refund at its discretion.</li>
                        </ul>
                    </Section>

                    <Section title="Recruiting Services" id="recruiting" testId="refund-recruit">
                        <p><b className="text-foreground">Contingency recruiting:</b> No placement, no fee. Placement fee is only due after a successful placement.</p>
                        <p><b className="text-foreground">Retained search (future):</b> Retainers are non-refundable once recruiting work has begun unless otherwise specified in the service agreement.</p>
                        {/* iter 146 — "60-day Hiring Success Guarantee" copy
                            removed pending legal review. Do NOT re-introduce
                            guarantee language without written sign-off. */}
                    </Section>

                    <Section title="Talent · Free Passport" id="talent-free" testId="refund-talent-free">
                        <p>The Talent Passport™ is free. No refund policy applies.</p>
                    </Section>

                    <Section title="Talent · Pro Subscription" id="talent-pro" testId="refund-talent-pro">
                        <ul className="list-disc pl-5 space-y-1.5">
                            <li>Users may cancel anytime.</li>
                            <li>Subscription remains active until the end of the current billing period.</li>
                            <li>No partial refunds.</li>
                        </ul>
                    </Section>

                    <Section title="Talent · Verification Purchases" id="talent-verify" testId="refund-talent-verify">
                        <p>Non-refundable once verification begins.</p>
                    </Section>

                    <div className="card-soft p-5">
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-muted-foreground">
                                Questions about a specific charge? <Link to="/contact" className="underline hover:text-foreground">Contact us</Link> and our team will review your account within one business day.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
